import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";

import { submitInvalidQuestionnaire as submitInvalidQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";

import { navigateToParentRoute } from "@teamprevent/common/routing/actions";

import { Loader } from "semantic-ui-react";

import { FileAnswer } from "@teamprevent/common/questionnaires/model";

import { ApplicationState } from "../../applicationState";
import { useOhCaseDetailsModel } from "../../crud/ohCase";
import { deleteConsultationEvidence, loadHealthSurveillanceConsultationForTask,
    saveNonClinicHealthSurveillanceConsultation, submitNonClinicHealthSurveillanceConsultation } from "../actions";
import { useCustomMedicalTypes } from "../../customMedicalTypes/hooks/useCustomMedicalTypes";

import { getOhCaseDocumentDownloadLink } from "../healthSurveillanceApi";

import { HealthSurveillanceTaskModel } from "../../task/model";

import { HealthSurveillanceConsultation, HealthSurveillanceConsultationKey } from "./HealthSurveillanceConsultation";

export const HealthSurveillanceConsultationForTask: React.FunctionComponent = () => {

    const dispatch = useDispatch();

    const customerId = useSelector((state: ApplicationState) => state.router.params!.customerId);
    const ohCaseId = useSelector((state: ApplicationState) => state.router.params!.ohCaseId);
    const taskId = useSelector((state: ApplicationState) => state.router.params!.taskId);
    const { ohCase, isLoaded: isOhCaseLoaded } = useOhCaseDetailsModel({ ohCaseId, customerId });
    const task = isOhCaseLoaded ? ohCase?.tasks?.find(t => t.id === taskId) as HealthSurveillanceTaskModel : undefined;

    const submitQuestionnaire = React.useCallback(async (isHavsTierThreeOrFour: boolean): Promise<boolean> => {
        await dispatch(submitNonClinicHealthSurveillanceConsultation(
            ohCaseId,
            taskId,
            isHavsTierThreeOrFour));
        return true;
    }, [dispatch, ohCaseId, taskId]);

    const submitInvalidQuestionnaire = React.useCallback(() => dispatch(submitInvalidQuestionnaireAction(HealthSurveillanceConsultationKey)), [dispatch]);

    const navigateToNextPage = React.useCallback(() => dispatch(navigateToParentRoute()), [dispatch]);

    const saveQuestionnaire = React.useCallback((questions: AnsweredQuestionWithId[], eTag: string | null) =>
        dispatch(saveNonClinicHealthSurveillanceConsultation(ohCaseId, taskId, questions, eTag)),
    [dispatch, ohCaseId, taskId]);

    const onDeleteEvidence = React.useCallback(
        (_fileId: string, _fileName: string, ohCaseDocumentId: string) => {
            return dispatch(deleteConsultationEvidence(ohCaseId, customerId, ohCaseDocumentId));
        },
        [customerId, dispatch, ohCaseId]);

    const getQuestionnaire = React.useCallback((abort: AbortController)  => {
        return dispatch(loadHealthSurveillanceConsultationForTask(ohCaseId, taskId, HealthSurveillanceConsultationKey, abort));
    }, [dispatch, ohCaseId, taskId]);

    const fileDownload = React.useCallback((fileAnswer: FileAnswer): string | null => {
        // If we have a OhCaseDocumentId set (which is the new way of storing files)
        // then return a URL that will use it
        if (fileAnswer.ohCaseDocumentId) {
            return getOhCaseDocumentDownloadLink(ohCaseId, fileAnswer);
        }

        // Otherwise return null here and let the underlying FilesQuestion use the default API url
        return null;
    }, [ohCaseId]);

    const {
        customMedicalTypes,
        refetch: loadCustomMedicalTypes
    } = useCustomMedicalTypes({ enable: false });

    if (isOhCaseLoaded === false || task === undefined) {
        return <Loader active />;
    }

    return (<HealthSurveillanceConsultation
        fileUploadApi={`/api/healthSurveillanceConsultation/${ohCaseId}/${customerId}/evidence`}
        questionnaireKey={HealthSurveillanceConsultationKey}
        onDeleteFile={onDeleteEvidence}
        getDownloadLink={fileDownload}
        getQuestionnaire={getQuestionnaire}
        medicalTypesForConsultation={task.medicalTypes}
        customMedicalTypes={customMedicalTypes}
        loadCustomMedicalTypes={loadCustomMedicalTypes}
        task={task}
        submitQuestionnaire={submitQuestionnaire}
        navigateToNextPage={navigateToNextPage}
        submitInvalidQuestionnaire={submitInvalidQuestionnaire}
        saveQuestionnaire={saveQuestionnaire}
        readonly={task?.completed ?? false}
        customerId={customerId}
        ohCaseId={ohCaseId}
    />);
};
